import { Injectable, inject, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, from, switchMap, take, timer } from 'rxjs';
import { StateSnackBarService } from './state-snack-bar.service';

/** Coordinates discovery and activation of new application versions. */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly updates = inject(SwUpdate);
  private readonly snackBar = inject(StateSnackBarService);
  private initialized = false;

  /** True once a downloaded version can be activated by the user. */
  readonly updateAvailable = signal(false);

  initialize(): void {
    if (this.initialized || !this.updates.isEnabled) {
      return;
    }

    this.initialized = true;
    this.updates.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => this.announceUpdate());

    // Wait until the worker has been registered, then ask for a new manifest
    // every six hours. It also checks during normal navigation requests.
    timer(60_000, 6 * 60 * 60 * 1000)
      .pipe(
        switchMap(() => from(this.updates.checkForUpdate())),
      )
      .subscribe({ error: () => undefined });
  }

  async applyUpdate(): Promise<void> {
    try {
      if (this.updates.isEnabled) {
        await this.updates.activateUpdate();
      }
      window.location.reload();
    } catch {
      this.snackBar.error({
        message: 'Das Update konnte nicht angewendet werden.',
        subMessage: 'Bitte lade die Seite erneut.',
      });
    }
  }

  private announceUpdate(): void {
    if (this.updateAvailable()) {
      return;
    }

    this.updateAvailable.set(true);
    this.snackBar
      .info({
        message: 'Eine neue Version ist verfügbar.',
        action: { text: 'Update' },
        duration: 0,
      })
      .onAction()
      .pipe(take(1))
      .subscribe(() => void this.applyUpdate());
  }
}
